'use client';

import { useRef } from 'react';
import { useSelector } from 'react-redux';
import classes from './dy.swiper.module.scss';
import Conditional from '@next-app/components/Conditional';
import ResponsiveImageClient from '@next-app/components/elements/ResponsiveImage/ResponsiveImageClient';
import {
  selectFeatureToggles,
  selectIsStoreSelected,
} from '@next-app/lib/features/InitSelectors';
import constants from '@next-app/constants/constants';
import ReviewStars from '@next-app/components/PDP/PDPReviews/ReviewStars';
import PDPCarouselButton from '@next-app/components/PDP/PDPCarousels/PDPCarouselButton';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';

interface CarouselProps {
  carouselId: string;
  dyCampaignData?: any;
  onAddedToCartModal?: boolean;
  showAddToCart?: boolean;
  pdpRedesignEnabled?: boolean;
}

const DySwiperWrapper = ({
  carouselId,
  dyCampaignData,
  onAddedToCartModal = false,
  showAddToCart,
  pdpRedesignEnabled = false,
}: CarouselProps) => {
  const { STORE_PICKUP_ONLY } = constants.LLConstants;
  const { sessionInfo } = useSelector((state: any) => state.init);
  const isStoreSelected = useSelector(selectIsStoreSelected);
  const featureToggles = useSelector(selectFeatureToggles);
  const enableStoreOnlyItems = featureToggles?.enableStoreOnlyItems;
  const { sessionInfo: { profile: { gsaUser = false } = {} } = {} } =
    sessionInfo || {};

  const campaignData = dyCampaignData?.dyCampaignData?.[0];
  const recommendations: any[] = campaignData?.dyProducts || [];
  const title = campaignData?.custom['Title'];

  const swiperRef = useRef<SwiperType | null>(null);
  const navigationPrevRef = useRef<HTMLDivElement | null>(null);
  const navigationNextRef = useRef<HTMLDivElement | null>(null);
  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  let breakpointSettings = {
    375: {
      spaceBetween: 15,
      slidesPerView: 2.1,
      slidesPerGroup: 2,
    },
    768: {
      spaceBetween: 15,
      slidesPerView: 3.25,
      slidesPerGroup: 3,
    },
    992: {
      spaceBetween: 30,
      slidesPerView: 4,
      slidesPerGroup: 4,
    },
    1560: {
      spaceBetween: 30,
      slidesPerView: 5,
      slidesPerGroup: 5,
    },
  };

  if (pdpRedesignEnabled && onAddedToCartModal) {
    breakpointSettings = {
      375: {
        spaceBetween: 15,
        slidesPerView: 2.15,
        slidesPerGroup: 2,
      },
      768: {
        spaceBetween: 15,
        slidesPerView: 4.55,
        slidesPerGroup: 4,
      },
      992: {
        spaceBetween: 30,
        slidesPerView: 4,
        slidesPerGroup: 4,
      },
      1560: {
        spaceBetween: 30,
        slidesPerView: 4,
        slidesPerGroup: 4,
      },
    };
  }

  return (
    <>
      <div
        className={`${classes['dySwiper']}${pdpRedesignEnabled && onAddedToCartModal ? ` ${classes.wider} wideSwiper` : ''}`}
        data-dy-variation-id={carouselId}
      >
        <div className={`${classes['swiper_outer']}`}>
          <h2
            className={`${classes[`swiper-title`]} text-${campaignData?.custom['Text Align']} ${campaignData?.custom['Title Color']} fs-${campaignData?.custom['Font']}${pdpRedesignEnabled && onAddedToCartModal ? ` ${classes['added-cart-modal']}` : ''}`}
          >
            {title}
          </h2>
          <Swiper
            grabCursor={true}
            breakpoints={breakpointSettings}
            navigation={{
              nextEl: `#next-${carouselId}`,
              prevEl: `#prev-${carouselId}`,
            }}
            onBeforeInit={(swiper) => {
              if (navigationPrevRef.current && navigationNextRef.current) {
                swiper.navigation.prevEl = navigationPrevRef.current;
                swiper.navigation.nextEl = navigationNextRef.current;
              }
            }}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Scrollbar, Navigation]}
            scrollbar={{
              el: '.swiper-scrollbar',
              draggable: true,
            }}
          >
            <div className={`${classes['swiper']}`}>
              <div className="swiper-wrapper">
                {recommendations.map((item: any, index: number) => (
                  <SwiperSlide
                    key={`recommendationSlide-${index}`}
                    className={`${classes['swiper-slide']}${pdpRedesignEnabled && onAddedToCartModal ? ` ${classes['added-cart-modal']}` : ''}`}
                  >
                    <div
                      className={`${classes['card_contents']}${onAddedToCartModal && pdpRedesignEnabled ? ` ${classes['added-cart-modal']}` : ''}`}
                    >
                      <a
                        href={item?.seoURL}
                        className={`${classes['rec_item']}${pdpRedesignEnabled && onAddedToCartModal ? ' d-inline-block' : ''}`}
                      >
                        <ResponsiveImageClient
                          desktopImagePath={item?.imageURL}
                          unoptimized={true}
                          altText={item?.altText}
                          isFullUrl={false}
                          fill={true}
                          className="position-static"
                        />
                        <Conditional if={item?.isNew}>
                          <div className={`bold ${classes['new-product']}`}>
                            NEW
                          </div>
                        </Conditional>
                        <Conditional if={item?.storeOnly}>
                          <span
                            className={`${classes['store-pickup']} ll-fs-12 ll-primary`}
                          >
                            {STORE_PICKUP_ONLY}
                          </span>
                        </Conditional>
                        <div
                          className={`${classes['rec_name']}`}
                          dangerouslySetInnerHTML={{ __html: item?.name }}
                        ></div>
                        <Conditional if={item?.isOnSale && !item?.hasGSAPrice}>
                          <p
                            className={`${classes['product-price-original']} ll-fs-16`}
                          >
                            <span
                              className={`${classes['product-price-sale']} ${pdpRedesignEnabled && onAddedToCartModal ? 'll-fs-14' : 'll-fs-16'}`}
                            >
                              {item?.salePriceRange}
                            </span>
                            <span
                              className={`${classes['product-price-reg']} ll-fs-16 text-decoration-line-through`}
                            >{`${item?.listPriceRange}`}</span>
                          </p>
                        </Conditional>
                        <Conditional if={gsaUser && item?.hasGSAPrice}>
                          <p
                            className={`${classes['product-price']} ${classes['product-price-gsa']} ll-fs-16`}
                          >
                            {item?.salePriceRange} GSA
                          </p>
                        </Conditional>
                        <Conditional if={!item?.hasGSAPrice && !item?.isOnSale}>
                          <p
                            className={`${classes['product-price']} ${pdpRedesignEnabled && onAddedToCartModal ? 'll-fs-14' : 'll-fs-16'}`}
                          >
                            {item?.listPriceRange}
                          </p>
                        </Conditional>
                        <div className={classes['product-detail']}>
                          <Conditional
                            if={!!(item?.showRating && item?.rating)}
                          >
                            <div className="mt-2 d-flex align-items-center">
                              <ReviewStars rating={item?.rating} />
                              <span className={classes['rating-count']}>
                                <span
                                  className={`${classes.ratingCount} ll-fs-12`}
                                >
                                  ({item?.reviewCount})
                                </span>
                              </span>
                            </div>
                          </Conditional>
                          <Conditional if={!!item?.presellDate}>
                            <div
                              className={`ll-fs-12 light ${classes['pre-sell-date']}`}
                            >
                              Available {item?.presellDate}
                            </div>
                          </Conditional>
                        </div>
                      </a>
                      <Conditional
                        if={
                          !!(
                            showAddToCart &&
                            !(
                              item?.storeOnly &&
                              (!enableStoreOnlyItems || !isStoreSelected)
                            )
                          )
                        }
                      >
                        <PDPCarouselButton
                          product={item}
                          customClasses={
                            'bottom-0 mb-ll-4 position-absolute start-0'
                          }
                          carouselId={carouselId}
                          onAddedToCartModal={onAddedToCartModal}
                          pdpRedesignEnabled={pdpRedesignEnabled}
                        />
                      </Conditional>
                      <Conditional
                        if={
                          item?.storeOnly &&
                          (!enableStoreOnlyItems || !isStoreSelected)
                        }
                      >
                        <div style={{ height: 40 }}></div>
                      </Conditional>
                    </div>
                  </SwiperSlide>
                ))}
              </div>
            </div>
            <div
              className={`swiper-scrollbar ${classes['swiper-scrollbar']}`}
            ></div>
          </Swiper>
        </div>
        <div
          id={`prev-${carouselId}`}
          className={`${classes['s-p']}`}
          onClick={handlePrev}
          ref={navigationPrevRef}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={`${pdpRedesignEnabled && onAddedToCartModal ? '8' : '18'}`}
            height={`${pdpRedesignEnabled && onAddedToCartModal ? '15' : '32'}`}
            viewBox="0 0 18 32"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.620569 0.683344C1.42239 -0.149656 2.71511 -0.149656 3.51693 0.683344L17.1151 14.8103C17.7533 15.4733 17.7533 16.5443 17.1151 17.2073L3.51693 31.3343C2.71511 32.1673 1.42239 32.1673 0.620569 31.3343C-0.18125 30.5013 -0.18125 29.1583 0.620569 28.3253L12.4678 16.0003L0.604208 3.67534C-0.181246 2.85934 -0.18125 1.49934 0.620569 0.683344Z"
              fill="white"
            />
          </svg>
        </div>
        <Conditional if={!(pdpRedesignEnabled && onAddedToCartModal)}>
          <br />
        </Conditional>
        <div
          id={`next-${carouselId}`}
          className={`${classes['s-n']}`}
          onClick={handleNext}
          ref={navigationNextRef}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={`${pdpRedesignEnabled && onAddedToCartModal ? '8' : '18'}`}
            height={`${pdpRedesignEnabled && onAddedToCartModal ? '15' : '32'}`}
            viewBox="0 0 18 32"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.620569 0.683344C1.42239 -0.149656 2.71511 -0.149656 3.51693 0.683344L17.1151 14.8103C17.7533 15.4733 17.7533 16.5443 17.1151 17.2073L3.51693 31.3343C2.71511 32.1673 1.42239 32.1673 0.620569 31.3343C-0.18125 30.5013 -0.18125 29.1583 0.620569 28.3253L12.4678 16.0003L0.604208 3.67534C-0.181246 2.85934 -0.18125 1.49934 0.620569 0.683344Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
    </>
  );
};

export default DySwiperWrapper;
